import { CandidateEditProfileEmpty, GetPostionResponse, Position } from 'candidate/types';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import debounce from 'lodash/debounce';

import { JobApiService } from 'jobs/JobApiService';
import { JobCategory } from 'jobs/types';
import { CountryCitySelectorStore } from 'shared/components/country-city-selector';
import { AutocompleteV2ViewModel } from 'shared/components/autocomplete-v2';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';

import { WorkTypes } from './brain/work-types_search.vm';
import { DesiredCategoryGroup, DesiredSubCategory } from './brain/sub_categories-search.vm';

type PositionAutocompleteItem = {
  id: number | string;
  name: string;
  slug: string;
};

export class DesiredPositionStore {
  position = '';
  salaryMin = '';
  salaryMax = '';
  salaryCurrency = '';

  readonly positionOptions = observable.array<Position>([]);
  readonly categories = observable.array<JobCategory>([]);
  readonly desiredCategories = observable.array<DesiredCategoryGroup>([]);
  readonly positionVM = new AutocompleteV2ViewModel<PositionAutocompleteItem>({
    inputValue: '',
  });
  readonly positionSearchState = new AsyncState<GetPostionResponse>();
  readonly location = new CountryCitySelectorStore();
  readonly workTypes = new WorkTypes();
  readonly countries = observable.array<{ id: number; name: string }>([]);
  readonly currencies = observable.array<{ id: string; name: string }>([]);

  private readonly debouncedFetchPositions = debounce((query: string) => {
    this.getPositions(query);
  }, 500);
  private activePositionSearchRequestId: number | null = null;
  private positionSearchRequestId = 0;

  constructor() {
    makeObservable(this, {
      position: observable,
      salaryMin: observable,
      salaryMax: observable,
      salaryCurrency: observable,
      categories: observable,
      desiredCategories: observable,
      countries: observable,
      currencies: observable,
      valid: computed,
      isPositionLoading: computed,
      desiredCountry: computed,
      desiredCity: computed,
      hasDesiredCategoriesValid: computed,
      positionAutocompleteOptions: computed,
      onSearchPosition: action.bound,
      onSelectPosition: action.bound,
      addDesiredCategory: action.bound,
      removeDesiredCategory: action.bound,
      onDesiredCategoryChange: action.bound,
      setSalaryMin: action.bound,
      setSalaryMax: action.bound,
      setDesiredCountry: action.bound,
      setSalaryCurrency: action.bound,
      update: action.bound,
    });

    this.addDesiredCategory();
  }

  get valid(): boolean {
    return Boolean(
      this.position.trim() &&
        this.hasDesiredCategoriesValid &&
        this.workTypes.values.length > 0 &&
        this.salaryMin.trim() &&
        this.salaryMax.trim() &&
        this.desiredCountry.trim() &&
        this.salaryCurrency.trim()
    );
  }

  get isPositionLoading(): boolean {
    return (
      this.positionSearchState.status === 'pending' &&
      this.activePositionSearchRequestId !== null
    );
  }

  get desiredCountry(): string {
    return this.location.countryId;
  }

  get desiredCity(): string {
    return this.location.cityLabel;
  }

  get hasDesiredCategoriesValid(): boolean {
    return (
      this.desiredCategories.length > 0 &&
      this.desiredCategories.every((group) => group.valid)
    );
  }

  get positionAutocompleteOptions(): PositionAutocompleteItem[] {
    const query = this.positionVM.inputValue.trim();
    const options = this.positionOptions.slice() as PositionAutocompleteItem[];

    if (
      query.length >= 2 &&
      !options.some((option) => option.name.toLowerCase() === query.toLowerCase())
    ) {
      options.unshift({
        id: `custom:${query}`,
        name: query,
        slug: '',
      });
    }

    return options;
  }

  onSearchPosition(query: string): void {
    this.position = query;
    this.positionVM.setInputValue(query);
    if (query.length < 2) {
      this.debouncedFetchPositions.cancel();
      this.positionSearchRequestId += 1;
      this.activePositionSearchRequestId = null;
      this.positionOptions.clear();
      return;
    }

    this.debouncedFetchPositions(query);
  }

  onSelectPosition(item: PositionAutocompleteItem): void {
    this.debouncedFetchPositions.cancel();
    this.positionSearchRequestId += 1;
    this.activePositionSearchRequestId = null;
    this.position = item.name;
    this.positionVM.select(item, item.name);
  }

  addDesiredCategory(): void {
    if (this.desiredCategories.length >= 3) {
      return;
    }

    this.desiredCategories.push(new DesiredCategoryGroup());
  }

  removeDesiredCategory(groupId: string): void {
    if (this.desiredCategories.length <= 1) {
      return;
    }

    const index = this.desiredCategories.findIndex((group) => group.id === groupId);
    if (index >= 0) {
      this.desiredCategories.splice(index, 1);
    }
  }

  onDesiredCategoryChange(groupId: string, value: string): void {
    const group = this.desiredCategories.find((item) => item.id === groupId);
    if (!group) {
      return;
    }

    group.setCategoryId(value);
    group.clearSpecializations();
    group.setOptions([]);

    if (!value) {
      return;
    }

    this.loadSubCategories(group, value);
  }

  setSalaryMin(value: string): void {
    this.salaryMin = value;
  }

  setSalaryMax(value: string): void {
    this.salaryMax = value;
  }

  setDesiredCountry(value: string): void {
    this.location.setCountryId(value);
  }

  setSalaryCurrency(value: string): void {
    this.salaryCurrency = value;
  }

  update(response: CandidateEditProfileEmpty): void {
    this.categories.replace(response.categories);
    this.countries.replace(response.countries);
    this.currencies.replace(
      Object.entries(response.currencies).map(([id, name]) => ({
        id,
        name,
      }))
    );

    this.workTypes.set(response.work_types);

    const profile = response.userdetail;
    this.position = profile.position?.label ?? '';
    this.positionVM.setValue(null);
    this.positionVM.setInputValue(this.position);
    this.salaryMin = String(profile.salary_min ?? '');
    this.salaryMax = String(profile.salary_max ?? '');
    this.location.setCountryId(String(profile.desired_country ?? ''));
    this.location.setCityLabel(profile.relocations?.[0]?.label ?? '');
    this.salaryCurrency = String(profile.salary_currency ?? '');

    this.workTypes.values.clear();
    profile.work_type.forEach((workType) => {
      const match = this.workTypes.workTypes.find((type) => type.value === workType.value);
      if (match) {
        this.workTypes.onSelect(match);
      }
    });

    const desiredCategories = profile.desired_category ?? [];
    const groups = desiredCategories.length
      ? desiredCategories.map((desiredCategory) => {
          const group = new DesiredCategoryGroup(
            String(desiredCategory?.category ?? '')
          );
          const selectedSubCategories: DesiredSubCategory[] = (
            desiredCategory?.sub_category ?? []
          ).map((subcategory) => ({
            id: Number(subcategory.value),
            name: subcategory.label,
            slug: '',
            status: 1,
          }));
          group.setSelected(selectedSubCategories);
          return group;
        })
      : [new DesiredCategoryGroup()];

    this.desiredCategories.replace(groups);
    groups.forEach((group) => {
      if (group.categoryId) {
        this.loadSubCategories(group, group.categoryId);
      }
    });
  }

  dispose(): void {
    this.debouncedFetchPositions.cancel();
    this.location.dispose();
  }

  private async getPositions(query: string): Promise<void> {
    if (query.length < 2) {
      return;
    }

    const requestId = ++this.positionSearchRequestId;
    this.activePositionSearchRequestId = requestId;

    await this.positionSearchState.run(
      () => HttpClient.post<GetPostionResponse>('/candidates/getPositions', { query }),
      {
        onSuccess: (response) => {
          if (response.status === 200) {
            runInAction(() => {
              if (requestId === this.activePositionSearchRequestId) {
                this.positionOptions.replace(response.response);
              }
            });
          }
        },
        onError: () => {
          runInAction(() => {
            if (requestId === this.activePositionSearchRequestId) {
              this.positionOptions.clear();
            }
          });
        },
        onFinally: () => {
          runInAction(() => {
            if (requestId === this.activePositionSearchRequestId) {
              this.activePositionSearchRequestId = null;
            }
          });
        },
      }
    );
  }

  private async loadSubCategories(group: DesiredCategoryGroup, categoryId: string): Promise<void> {
    await group.loadSubCategoriesState.run(
      async () => {
        const response = await JobApiService.getSubCategories(Number(categoryId));
        return response.status === 200 ? response.response : [];
      },
      {
        onSuccess: (subCategories) => {
          if (group.categoryId === categoryId) {
            group.setOptions(subCategories);
          }
        },
        onError: () => {
          if (group.categoryId === categoryId) {
            group.setOptions([]);
          }
        },
      }
    );
  }
}
